import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTransactions } from '@/hooks/useSubscriptions';
import type { TransactionFilters, GlobalSubscriptionFilters } from '../../../../shared/types';
import { WinclapLoader } from '@/components/ui/winclap-loader';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type SortField = 'Fecha' | 'Amount';
type SortDir = 'asc' | 'desc';

export function TransactionsTab({ globalFilters = {} }: { globalFilters?: GlobalSubscriptionFilters }) {
  const { t } = useTranslation();
  const [apiFilters, setApiFilters] = useState<TransactionFilters>({});
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('Fecha');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const activeFilters = useMemo<TransactionFilters>(() => {
    const merged: TransactionFilters = { ...apiFilters, search: search || undefined };
    if (globalFilters.dateFrom) merged.dateFrom = globalFilters.dateFrom;
    if (globalFilters.dateTo) merged.dateTo = globalFilters.dateTo;
    if (globalFilters.vendor) merged.vendor = globalFilters.vendor;
    if (globalFilters.department) merged.department = globalFilters.department;
    if (globalFilters.isAITool !== undefined) merged.isAITool = globalFilters.isAITool;
    return merged;
  }, [apiFilters, search, globalFilters]);

  const { data: transactions = [], isLoading } = useTransactions(activeFilters);

  // Frontend sort
  const sorted = useMemo(() => {
    return [...transactions].sort((a, b) => {
      const va = sortField === 'Fecha' ? a.Fecha : a.Amount;
      const vb = sortField === 'Fecha' ? b.Fecha : b.Amount;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transactions, sortField, sortDir]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const uniqueVendors = [...new Set(transactions.map((tx) => tx.Normalized_Vendor))].sort();
  const uniqueCategories = [...new Set(transactions.map((tx) => tx.Subscription_Category))].sort();

  const hasFilters = search || apiFilters.vendor || apiFilters.category || apiFilters.isAITool !== undefined || apiFilters.isAINativeRelated !== undefined;

  return (
    <div className="space-y-4 mt-4">
      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="text-xs text-muted-foreground mb-1 block">{t('common.ui.search')}</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={t('subscriptions.transactions.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-sm pl-8"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.transactions.filter_vendor')}</label>
            <Select
              value={apiFilters.vendor ?? 'all'}
              onValueChange={(v) => setApiFilters((f) => ({ ...f, vendor: v === 'all' ? undefined : v }))}
            >
              <SelectTrigger className="h-8 text-sm w-44">
                <SelectValue placeholder={t('subscriptions.transactionstab.all_vendors')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('subscriptions.transactions.filter_all')}</SelectItem>
                {uniqueVendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.transactions.filter_category')}</label>
            <Select
              value={apiFilters.category ?? 'all'}
              onValueChange={(v) => setApiFilters((f) => ({ ...f, category: v === 'all' ? undefined : v }))}
            >
              <SelectTrigger className="h-8 text-sm w-44">
                <SelectValue placeholder={t('subscriptions.transactionstab.all_categories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('subscriptions.transactions.filter_all')}</SelectItem>
                {uniqueCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.transactions.filter_ai_tool')}</label>
            <Select
              value={apiFilters.isAITool === undefined ? 'all' : apiFilters.isAITool ? 'yes' : 'no'}
              onValueChange={(v) => setApiFilters((f) => ({ ...f, isAITool: v === 'all' ? undefined : v === 'yes' }))}
            >
              <SelectTrigger className="h-8 text-sm w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('subscriptions.transactions.filter_all')}</SelectItem>
                <SelectItem value="yes">{t('subscriptions.transactions.filter_ai_tool')}</SelectItem>
                <SelectItem value="no">{t('subscriptions.transactions.filter_no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">{t('subscriptions.transactions.filter_ai_native')}</label>
            <Select
              value={apiFilters.isAINativeRelated === undefined ? 'all' : apiFilters.isAINativeRelated ? 'yes' : 'no'}
              onValueChange={(v) => setApiFilters((f) => ({ ...f, isAINativeRelated: v === 'all' ? undefined : v === 'yes' }))}
            >
              <SelectTrigger className="h-8 text-sm w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('subscriptions.transactions.filter_all')}</SelectItem>
                <SelectItem value="yes">{t('subscriptions.transactions.filter_ai_native')}</SelectItem>
                <SelectItem value="no">{t('subscriptions.transactions.filter_no')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={() => { setApiFilters({}); setSearch(''); }}>
              {t('subscriptions.transactions.clear')}
            </Button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <p className="text-sm font-medium">
            {isLoading ? t('subscriptions.transactions.loading') : t('subscriptions.transactions.showing', { n: sorted.length.toLocaleString() })}
            {sorted.length === 500 && <span className="text-xs text-muted-foreground ml-1">{t('subscriptions.transactions.showing_max')}</span>}
          </p>
        </div>
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <WinclapLoader size={48} className="text-foreground/25" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center py-16 gap-2">
              <p className="text-muted-foreground">{t('subscriptions.transactions.no_results')}</p>
              {hasFilters && (
                <Button variant="outline" size="sm" onClick={() => { setApiFilters({}); setSearch(''); }}>
                  {t('subscriptions.transactions.clear_filters')}
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                  <th
                    className="text-left py-2.5 px-4 font-medium cursor-pointer hover:text-foreground select-none"
                    onClick={() => toggleSort('Fecha')}
                  >
                    {t('subscriptions.transactions.col_date')} {sortField === 'Fecha' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th className="text-left py-2.5 px-4 font-medium">{t('subscriptions.transactions.col_vendor')}</th>
                  <th className="text-left py-2.5 px-4 font-medium">{t('subscriptions.transactions.col_category')}</th>
                  <th className="text-left py-2.5 px-4 font-medium">{t('subscriptions.transactions.col_user')}</th>
                  <th className="text-left py-2.5 px-4 font-medium">{t('subscriptions.transactions.col_dept')}</th>
                  <th
                    className="text-right py-2.5 px-4 font-medium cursor-pointer hover:text-foreground select-none"
                    onClick={() => toggleSort('Amount')}
                  >
                    {t('subscriptions.transactions.col_amount')} {sortField === 'Amount' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th className="text-center py-2.5 px-4 font-medium">{t('subscriptions.transactions.col_flags')}</th>
                  <th className="text-center py-2.5 px-4 font-medium">{t('subscriptions.transactions.col_link')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((txn) => (
                  <tr key={txn.Transaction_ID} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-4 text-muted-foreground text-xs whitespace-nowrap">{txn.Fecha}</td>
                    <td className="py-2.5 px-4">
                      <div>
                        <p className="font-medium">{txn.Normalized_Vendor}</p>
                        {txn.Merchant_Description !== txn.Normalized_Vendor && (
                          <p className="text-xs text-muted-foreground truncate max-w-40">{txn.Merchant_Description}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-muted-foreground whitespace-nowrap">{txn.Subscription_Category}</td>
                    <td className="py-2.5 px-4">
                      <p className="text-xs">{txn.User}</p>
                      <p className="text-xs text-muted-foreground">{txn.User_Email}</p>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-muted-foreground whitespace-nowrap">
                      {txn.NetSuite_Departamentos}
                    </td>
                    <td className="py-2.5 px-4 text-right font-semibold whitespace-nowrap">{fmt(txn.Amount)}</td>
                    <td className="py-2.5 px-4 text-center">
                      <div className="flex gap-1 justify-center flex-wrap">
                        {txn.Is_AI_Tool && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30">{t('subscriptions.transactionstab.ai_tool')}</Badge>
                        )}
                        {txn.Is_AI_Native_Related && (
                          <Badge variant="secondary" className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30">{t('subscriptions.transactionstab.ai_native')}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      {txn.Transaction_Link ? (
                        <a
                          href={txn.Transaction_Link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Ramp
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
